/**
 * Site shell shared by every area: black header (inline mobile-number login, or the signed-in
 * balance and Deposit), the icon category bar with Betslip and My Bets, the account drawer, the
 * login / sign-up sheet and the footer.
 *
 * `data-section` on the shell (and on <html>, for overlays) selects the section accent:
 * "sport" (green) on /sports*, otherwise "casino" (blue).
 */
import { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode, RefObject } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { version as appVersion } from "../../../package.json";
import type { SessionUser } from "../../shared/api.ts";
import { ApiRequestError, api } from "../../ui/api.ts";
import { Spinner, useToast } from "../../ui/components.tsx";
import { Wordmark } from "../../ui/brand.tsx";
import { formatAmount } from "../../ui/format.ts";
import type { OddsFormat } from "../../ui/format.ts";
import { ShellProvider, useAppState, useHideBalance, useOddsFormat, useShell, useWalletBalances } from "../state.tsx";
import type { ShellApi } from "../state.tsx";
import { Icon } from "./icons.tsx";
import type { IconName } from "./icons.tsx";
import { errorMessage } from "./lib.ts";
import type { LoginCarry } from "./pages/auth/LoginForm.tsx";

const LoginForm = lazy(() => import("./pages/auth/LoginForm.tsx"));
const RegisterForm = lazy(() => import("./pages/auth/RegisterForm.tsx"));

// ---------------------------------------------------------------- navigation model

type CategoryKey = "home" | "sport" | "live" | "casino" | "slots" | "crash" | "table" | "instant" | "racing" | "numbers";

const CATEGORIES: { key: CategoryKey; to: string; label: string; icon: IconName }[] = [
  { key: "home", to: "/", label: "home", icon: "home" },
  { key: "sport", to: "/sports", label: "sport", icon: "sports" },
  { key: "live", to: "/sports/live", label: "live", icon: "tv" },
  { key: "casino", to: "/casino", label: "casino", icon: "chip" },
  { key: "slots", to: "/casino?category=slots", label: "slots", icon: "slots" },
  { key: "crash", to: "/casino/crash", label: "crash", icon: "rocket" },
  { key: "table", to: "/casino?category=table", label: "table games", icon: "cards" },
  { key: "instant", to: "/casino?category=instant", label: "quick games", icon: "bolt" },
  { key: "racing", to: "/horse-racing", label: "horse racing", icon: "horse" },
  { key: "numbers", to: "/lucky-numbers", label: "lucky numbers", icon: "numbers" },
];

function activeCategory(pathname: string, search: string): CategoryKey | null {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/horse-racing")) return "racing";
  if (pathname.startsWith("/lucky-numbers")) return "numbers";
  if (pathname.startsWith("/sports/live")) return "live";
  if (pathname.startsWith("/sports")) return "sport";
  if (pathname.startsWith("/casino/crash")) return "crash";
  if (pathname === "/casino" || pathname === "/casino/") {
    const c = new URLSearchParams(search).get("category");
    if (c === "slots" || c === "table" || c === "instant" || c === "crash") return c;
    return "casino";
  }
  if (pathname.startsWith("/casino/slot-")) return "slots";
  if (pathname.startsWith("/casino")) return "casino";
  return null;
}

const ACCOUNT_MENU: { to: string; label: string; icon: IconName }[] = [
  { to: "/account/profile", label: "My Profile", icon: "user" },
  { to: "/sports/bets", label: "My Bets", icon: "ticket" },
  { to: "/wallet/transactions", label: "Transaction History", icon: "receipt" },
  { to: "/casino/history", label: "Casino History", icon: "chip" },
  { to: "/account/responsible-gaming", label: "Responsible Gaming", icon: "shield" },
  { to: "/account/verification", label: "Document Verification", icon: "id" },
];

const FOOTER_GROUPS = [
  {
    title: "Help & support",
    links: [
      { to: "/support", label: "Contact Us" },
      { to: "/help", label: "Frequently asked questions" },
      { to: "/how-to-bet", label: "How To Bet" },
    ],
  },
  {
    title: "Betting",
    links: [
      { to: "/rules/sports", label: "Betting Rules" },
      { to: "/responsible-gambling", label: "Responsible Gaming" },
      { to: "/terms", label: "Terms & Conditions" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/licensing", label: "Company information" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/privacy#paia", label: "PAIA Manual" },
    ],
  },
];

const RG_PATHS = ["/account/responsible-gaming", "/account/limits", "/account/take-a-break", "/account/self-exclude", "/account/activity"];

function isMenuCurrent(to: string, pathname: string): boolean {
  if (to === "/account/profile") return pathname === "/account" || pathname === "/account/profile" || pathname === "/account/security" || pathname === "/account/close";
  if (to === "/account/responsible-gaming") return RG_PATHS.includes(pathname);
  return pathname === to;
}

export function fullName(user: SessionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.username;
}

// ---------------------------------------------------------------- small hooks

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

const DESKTOP = "(min-width: 1024px)";

/** Focus trap, Escape to close, scroll lock and focus restore for drawers and sheets. */
function useDialog(open: boolean, onClose: () => void, ref: RefObject<HTMLElement | null>) {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const items = ref.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
      prev?.focus?.();
    };
  }, [open, ref]);
}

// ---------------------------------------------------------------- layout

interface AuthState {
  mode: "login" | "register";
  carry?: LoginCarry;
}

export function Layout({ children }: { children: ReactNode }) {
  const { user } = useAppState();
  const location = useLocation();
  const toast = useToast();
  const section = location.pathname.startsWith("/lucky-numbers") ? "numbers" : location.pathname.startsWith("/sports") || location.pathname.startsWith("/horse-racing") ? "sport" : "casino";
  const [drawer, setDrawer] = useState<"account" | "menu" | null>(null);
  const [auth, setAuth] = useState<AuthState | null>(null);

  useEffect(() => {
    document.documentElement.dataset.section = section;
  }, [section]);

  // New page: close overlays and start at the top (unless jumping to an in-page anchor).
  useEffect(() => {
    setDrawer(null);
    setAuth(null);
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // One-off message passed through navigation state (e.g. after closing an account).
  useEffect(() => {
    const flash = (location.state as { flash?: { text?: unknown } } | null)?.flash;
    if (flash && typeof flash.text === "string") toast(flash.text, "info");
  }, [location.key, location.state, toast]);

  const shell = useMemo<ShellApi>(
    () => ({
      openAuth: (mode) => {
        setDrawer(null);
        setAuth({ mode });
      },
      openAccountMenu: () => setDrawer(user ? "account" : "menu"),
    }),
    [user],
  );
  const openLoginWith = useCallback((carry: LoginCarry) => setAuth({ mode: "login", carry }), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);
  const closeAuth = useCallback(() => setAuth(null), []);

  return (
    <ShellProvider value={shell}>
      <div className="site" data-section={section}>
        <a className="skip-link" href="#main">
          Skip to main content
        </a>
        <div className="site-top">
          <SiteHeader onMenu={() => setDrawer(user ? "account" : "menu")} drawerOpen={drawer !== null} onLoginCarry={openLoginWith} />
          <CategoryBar />
        </div>
        <main id="main" className="site-main" tabIndex={-1}>
          {children}
        </main>
        <SiteFooter />
        {drawer === "account" && user ? <AccountDrawer onClose={closeDrawer} /> : null}
        {drawer === "menu" && !user ? <MenuDrawer onClose={closeDrawer} /> : null}
        {auth ? <AuthSheet state={auth} onClose={closeAuth} onSwitch={(mode) => setAuth({ mode })} /> : null}
      </div>
    </ShellProvider>
  );
}

// ---------------------------------------------------------------- header

function SiteHeader({ onMenu, drawerOpen, onLoginCarry }: { onMenu: () => void; drawerOpen: boolean; onLoginCarry: (c: LoginCarry) => void }) {
  const { user, sessionLoaded, shopMode } = useAppState();
  const desktop = useMediaQuery(DESKTOP);
  return (
    <header className="site-header hdr">
      <div className="hdr__inner">
        <button
          type="button"
          className="hdr__burger"
          onClick={onMenu}
          aria-label={user ? "Open account menu" : "Open menu"}
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
        >
          <Icon name="menu" size={28} />
        </button>
        <Link to="/" className="hdr__brand" aria-label="WorldBet24 home">
          <Wordmark />
        </Link>
        <div className="hdr__right">
          {user?.shopOwnerId && desktop ? <Link to="/shop" className="btn btn-secondary btn-sm">Shop dashboard</Link> : null}
          {!sessionLoaded ? (
            <span className="hdr__pending" aria-hidden="true" />
          ) : user ? (
            <SignedInHeader user={user} desktop={desktop} onMenu={onMenu} />
          ) : shopMode ? <Link to="/login?next=/casino&shop=1" className="hdr-btn hdr-btn--login">Player login</Link> : desktop ? (
            <InlineLogin onLoginCarry={onLoginCarry} />
          ) : (
            <MobileAuthButtons />
          )}
        </div>
      </div>
    </header>
  );
}

/** "+27 | Mobile Number", "Enter Password" (eye), Login, Sign Up; Forgot links underneath. */
function InlineLogin({ onLoginCarry }: { onLoginCarry: (c: LoginCarry) => void }) {
  const { refreshSession } = useAppState();
  const { openAuth } = useShell();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field: "mobile" | "password" | null; text: string } | null>(null);
  const errorId = useId();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const digits = mobile.replace(/\D/g, "");
    const national = digits.length === 11 && digits.startsWith("27") ? digits.slice(2) : digits.startsWith("0") ? digits.slice(1) : digits;
    if (!/^[6-8]\d{8}$/.test(national)) {
      setError({ field: "mobile", text: "Enter a valid mobile number" });
      document.getElementById("hdr-mobile")?.focus();
      return;
    }
    if (!password) {
      setError({ field: "password", text: "Enter your password" });
      document.getElementById("hdr-password")?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    const identifier = `+27${national}`;
    try {
      const res = await api.post<{ mfaRequired?: boolean; user?: SessionUser | null }>("/api/auth/login", { identifier, password });
      if (res?.mfaRequired && !res.user) {
        onLoginCarry({ identifier, password, step: "mfa" });
        return;
      }
      await refreshSession();
      setPassword("");
      setMobile("");
    } catch (err) {
      if (err instanceof ApiRequestError && (err.code === "turnstile_required" || err.code === "turnstile_failed")) {
        onLoginCarry({ identifier, password, needsCheck: true });
      } else if (err instanceof ApiRequestError && err.code === "mfa_required") {
        onLoginCarry({ identifier, password, step: "mfa" });
      } else {
        setError({ field: null, text: errorMessage(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="hdr-login" onSubmit={onSubmit} noValidate aria-label="Log in">
      <div className={`hdr-login__box hdr-login__box--mobile${error?.field === "mobile" ? " is-invalid" : ""}`}>
        <span className="hdr-login__prefix" aria-hidden="true">
          +27
        </span>
        <label htmlFor="hdr-mobile" className="sr-only">
          Mobile number (+27)
        </label>
        <input
          id="hdr-mobile"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="Mobile Number"
          value={mobile}
          maxLength={13}
          onChange={(e) => {
            setMobile(e.target.value.replace(/[^\d\s]/g, ""));
            setError(null);
          }}
          aria-invalid={error?.field === "mobile" ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      <div className={`hdr-login__box hdr-login__box--pw${error?.field === "password" ? " is-invalid" : ""}`}>
        <label htmlFor="hdr-password" className="sr-only">
          Password
        </label>
        <input
          id="hdr-password"
          type={show ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Enter Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
          }}
          aria-invalid={error?.field === "password" ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        <button type="button" className="hdr-login__eye" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"} aria-pressed={show}>
          <Icon name={show ? "eye-off" : "eye"} size={22} />
        </button>
      </div>
      <button type="submit" className="hdr-btn hdr-btn--login" disabled={busy} aria-busy={busy || undefined}>
        Login
      </button>
      <button type="button" className="hdr-btn hdr-btn--signup" onClick={() => openAuth("register")}>
        Sign Up
      </button>
      <div className="hdr-login__links">
        {error ? (
          <span id={errorId} className="hdr-login__error" role="alert">
            {error.text}
          </span>
        ) : (
          <>
            <Link to="/forgot-username" className="hdr-login__link">
              Forgot Username
            </Link>
            <Link to="/forgot-password" className="hdr-login__link">
              Forgot Password?
            </Link>
          </>
        )}
      </div>
    </form>
  );
}

function MobileAuthButtons() {
  const { openAuth } = useShell();
  return (
    <div className="hdr-auth">
      <button type="button" className="hdr-btn hdr-btn--login" onClick={() => openAuth("login")}>
        Login
      </button>
      <button type="button" className="hdr-btn hdr-btn--signup" onClick={() => openAuth("register")}>
        Sign Up
      </button>
    </div>
  );
}

function SignedInHeader({ user, desktop, onMenu }: { user: SessionUser; desktop: boolean; onMenu: () => void }) {
  return (
    <div className="hdr-user">
      {desktop ? (
        <button type="button" className="hdr-user__who" onClick={onMenu} aria-label={`Account menu for ${fullName(user)}`}>
          <Icon name="avatar" size={44} className="hdr-user__avatar" />
          <span className="hdr-user__text">
            <span className="hdr-user__welcome">Welcome</span>
            <span className="hdr-user__name">{fullName(user)}</span>
          </span>
        </button>
      ) : null}
      {user.shopOwnerId ? !desktop ? <Link to="/shop" className="hdr-btn hdr-btn--login">Shop</Link> : null : <BalanceBox />}
      {desktop ? (
        <button type="button" className="hdr-chevron" onClick={onMenu} aria-label="Open account menu" aria-haspopup="dialog">
          <Icon name="chevron-down" size={22} />
        </button>
      ) : null}
      {user.shopPlayer || user.shopOwnerId ? <span className="small muted">{user.shopOwnerId ? 'Shop manager' : 'Cashier wallet'}</span> : <Link to="/wallet/deposit" className="hdr-btn hdr-btn--deposit">
        Deposit
      </Link>}
    </div>
  );
}

function BalanceBox() {
  const { balances, refreshBalances } = useAppState();
  const [hidden] = useHideBalance();
  const [refreshing, setRefreshing] = useState(false);
  return (
    <div className="hdr-bal">
      <div className="hdr-bal__top">
        <span>Balance</span>
        <button
          type="button"
          className={`hdr-bal__refresh${refreshing ? " is-spinning" : ""}`}
          aria-label="Refresh balance"
          onClick={async () => {
            setRefreshing(true);
            await refreshBalances();
            setRefreshing(false);
          }}
        >
          <Icon name="refresh" size={14} />
        </button>
      </div>
      <BalanceAmount minor={balances?.available ?? null} hidden={hidden} className="hdr-bal__amt" />
    </div>
  );
}

/** "R 0.68" with the R in the section accent; "R ****" when hidden; "R –" until loaded. */
export function BalanceAmount({ minor, hidden, className }: { minor: number | null; hidden?: boolean; className?: string }) {
  return (
    <span className={`bal-amt tabular ${className ?? ""}`} aria-live="polite">
      <span className="bal-amt__r">R</span> {hidden ? <span aria-label="hidden">****</span> : minor == null ? "–" : formatAmount(minor)}
    </span>
  );
}

// ---------------------------------------------------------------- category bar

// Betslip contract with the sportsbook (src/web/sports): the slip persists its selections in
// sessionStorage and announces every change with a window event carrying { count }; it opens on
// `?betslip=1` or on the "wb24:open-betslip" window event.
const BETSLIP_COUNT_EVENT = "wb24:betslip";
const BETSLIP_OPEN_EVENT = "wb24:open-betslip";
const BETSLIP_STORAGE_KEY = "wb24.sports.betslip.v1";

function storedSlipCount(): number {
  try {
    const raw = sessionStorage.getItem(BETSLIP_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { v?: unknown; lines?: unknown }) : null;
    return parsed && parsed.v === 1 && Array.isArray(parsed.lines) ? parsed.lines.length : 0;
  } catch {
    return 0;
  }
}

function useBetslipCount(): number {
  const [count, setCount] = useState(storedSlipCount);
  useEffect(() => {
    const on = (e: Event) => {
      const n = (e as CustomEvent<{ count?: unknown }>).detail?.count;
      if (typeof n === "number" && Number.isFinite(n)) setCount(Math.max(0, Math.floor(n)));
    };
    window.addEventListener(BETSLIP_COUNT_EVENT, on);
    return () => window.removeEventListener(BETSLIP_COUNT_EVENT, on);
  }, []);
  return count;
}

function CategoryBar() {
  const { user, shopMode } = useAppState();
  const location = useLocation();
  const active = activeCategory(location.pathname, location.search);
  const listRef = useRef<HTMLUListElement>(null);
  const slipCount = useBetslipCount();

  // Keep the active item in view when the bar scrolls sideways (mobile).
  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!list || !el || list.scrollWidth <= list.clientWidth) return;
    const left = el.offsetLeft - list.clientWidth / 2 + el.clientWidth / 2;
    list.scrollTo({ left: Math.max(0, left) });
  }, [active]);

  return (
    <nav className="catbar" aria-label="Categories">
      <ul className="catbar__list" ref={listRef}>
        {CATEGORIES.filter(c => !shopMode || !['sport', 'live', 'racing'].includes(c.key)).map((c) => (
          <li key={c.key}>
            <Link to={shopMode && c.key === 'home' ? user?.shopOwnerId ? '/shop' : '/casino' : c.to} className="catbar__item" aria-current={active === c.key ? "page" : undefined}>
              <Icon name={c.icon} size={26} />
              <span>{c.label}</span>
            </Link>
          </li>
        ))}
      </ul>
      {!shopMode ? <div className="catbar__right">
        <Link
          to="/sports?betslip=1"
          className="catbar__text"
          onClick={(e) => {
            // On sports pages the slip is already mounted: ask it to open instead of navigating.
            if (!location.pathname.startsWith("/sports")) return;
            e.preventDefault();
            window.dispatchEvent(new Event(BETSLIP_OPEN_EVENT));
          }}
          aria-label={`Betslip, ${slipCount} selection${slipCount === 1 ? "" : "s"}`}
        >
          <Icon name="ticket" size={22} className="catbar__text-icon" />
          <span className="catbar__text-label">Betslip</span>
          <span className="catbar__count" aria-hidden="true">
            {slipCount}
          </span>
        </Link>
        {user ? (
          <Link to="/sports/bets" className="catbar__text catbar__text--bets">
            My Bets
          </Link>
        ) : null}
      </div> : null}
    </nav>
  );
}

// ---------------------------------------------------------------- account drawer & menu

export function OddsFormatSelect({ id }: { id: string }) {
  const [format, setFormat] = useOddsFormat();
  return (
    <div className="odds-format">
      <label htmlFor={id}>Odds format</label>
      <select id={id} className="select" value={format} onChange={(e) => setFormat(e.target.value as OddsFormat)}>
        <option value="decimal">Decimal</option>
        <option value="fractional">Fractional</option>
      </select>
    </div>
  );
}

/**
 * Name, account number, balance card (eye toggle, breakdown), Deposit / Withdraw and the account
 * menu cards. Used in the drawer and as the left column of the account pages.
 */
export function AccountPanel({ onNavigate, onClose, titleId, cashWallet = false }: { onNavigate?: () => void; onClose?: () => void; titleId?: string; cashWallet?: boolean }) {
  const { user, signOut, shopMode } = useAppState();
  const { balances, refreshBalances, label: balanceLabel } = useWalletBalances(cashWallet);
  const navigate = useNavigate();
  const location = useLocation();
  const [hidden, setHidden] = useHideBalance();
  const [breakdown, setBreakdown] = useState(false);
  const cashBreakdown = useWalletBalances(breakdown && !!user?.ownerTestMode && !cashWallet);
  const [refreshing, setRefreshing] = useState(false);
  const breakdownId = useId();
  const oddsId = useId();
  if (!user) return null;
  const go = () => onNavigate?.();

  return (
    <div className="acct-panel">
      <div className="acct-panel__head">
        <div className="acct-panel__who">
          <Link to={user.shopPlayer ? '/casino' : '/account/profile'} className="acct-panel__name" onClick={go} id={titleId}>
            {fullName(user)}
            <Icon name="chevron-right" size={20} />
          </Link>
          <p className="acct-panel__number">
            {user.shopPlayer ? 'Player: ' : 'Account Number: '}<strong className="tabular">{user.shopPlayer ? user.username : user.accountNumber}</strong>
          </p>
        </div>
        {onClose ? (
          <button type="button" className="acct-panel__close" onClick={onClose} aria-label="Close account menu">
            <Icon name="close" size={22} />
          </button>
        ) : null}
      </div>

      <div className="acct-bal">
        <div className="acct-bal__card">
          <div className="acct-bal__label">
            {balanceLabel}
            <button
              type="button"
              className={`acct-bal__icon${refreshing ? " is-spinning" : ""}`}
              aria-label="Refresh balance"
              onClick={async () => {
                setRefreshing(true);
                await refreshBalances();
                setRefreshing(false);
              }}
            >
              <Icon name="refresh" size={15} />
            </button>
          </div>
          <BalanceAmount minor={balances?.available ?? null} hidden={hidden} className="acct-bal__amt" />
          <button type="button" className="acct-bal__eye" onClick={() => setHidden(!hidden)} aria-pressed={hidden} aria-label={hidden ? "Show balance" : "Hide balance"}>
            <Icon name={hidden ? "eye" : "eye-off"} size={24} />
          </button>
        </div>
        <button
          type="button"
          className="acct-bal__more"
          aria-expanded={breakdown}
          aria-controls={breakdownId}
          aria-label="Balance breakdown"
          onClick={() => setBreakdown((b) => !b)}
        >
          <Icon name="chevron-down" size={20} className={breakdown ? "is-flipped" : undefined} />
        </button>
      </div>
      {breakdown ? (
        <dl className="acct-bal__breakdown" id={breakdownId}>
          {user.ownerTestMode && !cashWallet ? <div>
            <dt><Link to="/wallet/transactions?wallet=cash" onClick={go}>Cash balance</Link></dt>
            <dd><BalanceAmount minor={cashBreakdown.balances?.available ?? null} hidden={hidden} /></dd>
          </div> : null}
          <div>
            <dt>Available</dt>
            <dd>
              <BalanceAmount minor={balances?.available ?? null} hidden={hidden} />
            </dd>
          </div>
          <div>
            <dt>In open bets</dt>
            <dd>
              <BalanceAmount minor={balances?.reserved ?? null} hidden={hidden} />
            </dd>
          </div>
          {!shopMode ? <div>
            <dt>Pending withdrawal</dt>
            <dd>
              <BalanceAmount minor={balances?.pendingWithdrawal ?? null} hidden={hidden} />
            </dd>
          </div> : null}
        </dl>
      ) : null}

      {shopMode ? <p className="small muted">{user.shopPlayer ? 'Your cashier handles credit and cash payouts. This play account locks when its balance is too low to play.' : 'Manage player credit and cash from your shop dashboard.'}</p> : <div className="acct-panel__actions">
        <Link to="/wallet/deposit" className="acct-btn acct-btn--deposit" onClick={go}>
          Deposit
        </Link>
        <Link to="/wallet/withdraw" className="acct-btn acct-btn--withdraw" onClick={go}>
          Withdraw
        </Link>
      </div>}

      <nav aria-label="Account">
        <ul className="acct-menu">
          {user.shopOwnerId ? <li><Link to="/shop" className="acct-menu__item" onClick={go}><Icon name="chart" size={22} /><span>Shop dashboard</span><Icon name="chevron-right" size={20} className="acct-menu__chev" /></Link></li> : null}
          {ACCOUNT_MENU.filter(m => (!shopMode || !m.to.startsWith('/sports')) && (!user.shopPlayer || !['/account/profile', '/account/verification'].includes(m.to))).map((m) => (
            <li key={m.to}>
              <Link
                to={cashWallet && user.ownerTestMode && m.to === "/wallet/transactions" ? `${m.to}?wallet=cash` : m.to}
                className="acct-menu__item"
                onClick={go}
                aria-current={isMenuCurrent(m.to, location.pathname) ? "page" : undefined}
              >
                <Icon name={m.icon} size={22} />
                <span>{m.label}</span>
                <Icon name="chevron-right" size={20} className="acct-menu__chev" />
              </Link>
            </li>
          ))}
        </ul>
        <ul className="acct-menu">
          <li>
            <Link to="/support" className="acct-menu__item" onClick={go} aria-current={location.pathname === "/support" ? "page" : undefined}>
              <Icon name="message" size={22} />
              <span>Contact Support</span>
              <Icon name="chevron-right" size={20} className="acct-menu__chev" />
            </Link>
          </li>
          <li>
            <button
              type="button"
              className="acct-menu__item"
              onClick={async () => {
                go();
                await signOut();
                void navigate("/", { replace: true });
              }}
            >
              <Icon name="logout" size={22} />
              <span>Log Out</span>
              <Icon name="chevron-right" size={20} className="acct-menu__chev" />
            </button>
          </li>
        </ul>
      </nav>

      {!shopMode ? <OddsFormatSelect id={oddsId} /> : null}
    </div>
  );
}

function AccountDrawer({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(true, onClose, ref);
  return (
    <div className="overlay overlay--drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="drawer wb-light" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref}>
        <AccountPanel onNavigate={onClose} onClose={onClose} titleId={titleId} />
      </div>
    </div>
  );
}

/** Signed-out menu: Login / Sign Up, the categories and help links. */
function MenuDrawer({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { openAuth } = useShell();
  const { shopMode } = useAppState();
  const oddsId = useId();
  const active = activeCategory(location.pathname, location.search);
  useDialog(true, onClose, ref);
  return (
    <div className="overlay overlay--drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="drawer wb-light" role="dialog" aria-modal="true" aria-label="Menu" tabIndex={-1} ref={ref}>
        <div className="acct-panel">
          <div className="acct-panel__head">
            <span className="drawer__brand">
              <Wordmark />
            </span>
            <button type="button" className="acct-panel__close" onClick={onClose} aria-label="Close menu">
              <Icon name="close" size={22} />
            </button>
          </div>
          <div className="acct-panel__actions">
            <button type="button" className="acct-btn acct-btn--deposit" onClick={() => openAuth("login")}>
              Login
            </button>
            {!shopMode ? <button type="button" className="acct-btn acct-btn--withdraw" onClick={() => openAuth("register")}>
              Sign Up
            </button> : null}
          </div>
          <nav aria-label="Menu">
            <ul className="acct-menu">
              {CATEGORIES.filter(c => !shopMode || !['sport', 'live', 'racing'].includes(c.key)).map((c) => (
                <li key={c.key}>
                  <Link to={c.to} className="acct-menu__item" onClick={onClose} aria-current={active === c.key ? "page" : undefined}>
                    <Icon name={c.icon} size={22} />
                    <span className="acct-menu__cap">{c.label}</span>
                    <Icon name="chevron-right" size={20} className="acct-menu__chev" />
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="acct-menu">
              {[
                { to: "/how-to-bet", label: "How To Bet", icon: "help" as IconName },
                { to: "/support", label: "Contact Us", icon: "message" as IconName },
                { to: "/responsible-gambling", label: "Responsible Gaming", icon: "shield" as IconName },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="acct-menu__item" onClick={onClose}>
                    <Icon name={l.icon} size={22} />
                    <span>{l.label}</span>
                    <Icon name="chevron-right" size={20} className="acct-menu__chev" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          {!shopMode ? <OddsFormatSelect id={oddsId} /> : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- login / sign-up sheet

function AuthSheet({ state, onClose, onSwitch }: { state: AuthState; onClose: () => void; onSwitch: (mode: "login" | "register") => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(true, onClose, ref);
  const { shopMode } = useAppState();
  const login = shopMode || state.mode === "login";
  return (
    <div className="overlay overlay--sheet" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`sheet wb-light${login ? "" : " sheet--wide"}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref}>
        <div className="sheet__head">
          <h2 id={titleId}>{login ? "Login" : "Sign Up"}</h2>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={22} />
          </button>
        </div>
        <div className="sheet__body">
          <Suspense fallback={<Spinner />}>
            {login ? <LoginForm idPrefix="sheet-login-" carry={state.carry} onSuccess={onClose} /> : <RegisterForm idPrefix="sheet-reg-" />}
          </Suspense>
          {!shopMode ? <p className="sheet__switch">
            {login ? "Don't have an account?" : "Already registered?"}{" "}
            <button type="button" className="link-button" onClick={() => onSwitch(login ? "register" : "login")}>
              {login ? "Sign Up" : "Login"}
            </button>
          </p> : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- footer

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return <time dateTime={now.toISOString()}>{now.toLocaleTimeString("en-GB", { hour12: false })}</time>;
}

function SiteFooter() {
  const { shopMode } = useAppState();
  return (
    <footer className="site-footer ftr">
      <div className="ftr__inner">
        <div className="ftr__band">
          <Wordmark />
          <span className="ftr__18" aria-label="18 plus only">
            18
          </span>
        </div>
        <nav className="ftr__navigation" aria-label="Footer">
          {FOOTER_GROUPS.map((group) => (
            <div className="ftr__group" key={group.title}>
              <h2>{group.title}</h2>
              <ul className="ftr__links">
                {group.links.filter(link => !shopMode || !['/rules/sports', '/how-to-bet'].includes(link.to)).map((link) => (
                  <li key={link.to}>
                    <Link to={link.to}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="ftr__bottom">
          <span>
            Version: {appVersion} | Current time: <Clock />
          </span>
          <span>© {new Date().getFullYear()} WorldBet24</span>
        </div>
      </div>
    </footer>
  );
}
